import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import {
  buildManagementCompanyCreateHoa,
  getDirectoryNameError,
  getEmailValidationError,
  managementCompanyToHoaSnapshot,
  prepareManagementCompanyPayload,
  resolveEntityId,
  uploadInstructionFiles,
} from '../HoaDirectory/HoaDirectoryHelper';
import ManagementCompanyFormFields from './ManagementCompanyFormFields';
import HoaBlockSkeleton from './HoaBlockSkeleton';

const DirectoryManagementCompanyModal = ({
  show,
  onHide,
  mode = 'create',
  managementCompanyId = null,
  initialName = '',
  /** When set, ordering platform is saved on this association (not the mgmt company). */
  associationId = null,
  initialOrderingPlatform = '',
  /** Order picker context: plain name field, no directory autocomplete. */
  simpleForm = false,
  onSaved,
}) => {
  const isEdit = mode === 'edit';
  const useNameInput = !isEdit || simpleForm;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoa, setHoa] = useState(() => (isEdit ? null : buildManagementCompanyCreateHoa(initialName)));
  const [managementCompanyContacts, setManagementCompanyContacts] = useState([]);
  const [managementCompanyFiles, setManagementCompanyFiles] = useState([]);
  const [pendingManagementFiles, setPendingManagementFiles] = useState([]);
  const [removedManagementFileIds, setRemovedManagementFileIds] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) return;

    setErrors({});
    setSaving(false);
    setPendingManagementFiles([]);
    setRemovedManagementFileIds([]);

    if (!isEdit) {
      setHoa(buildManagementCompanyCreateHoa(initialName));
      setManagementCompanyContacts([]);
      setManagementCompanyFiles([]);
      setLoading(false);
      return;
    }

    const id = resolveEntityId(managementCompanyId);
    if (!id) {
      setHoa(null);
      setManagementCompanyContacts([]);
      setManagementCompanyFiles([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setHoa(null);

    (async () => {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.MANAGEMENT_COMPANY_WITH_ID(id),
        method: 'GET',
      });

      if (cancelled) return;

      if (error) {
        toast.error(error);
        setLoading(false);
        return;
      }

      setHoa({
        ...managementCompanyToHoaSnapshot(response),
        orderingPlatform: initialOrderingPlatform || '',
      });
      setManagementCompanyContacts(response.contacts || []);
      setManagementCompanyFiles(response.orderingInstructionFiles || []);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [show, isEdit, managementCompanyId, initialName, initialOrderingPlatform]);

  const updateHoa = (fields = {}) => {
    setHoa(prev => ({ ...prev, ...fields }));
    if (Object.keys(errors).length) setErrors({});
  };

  const mergeContact = (list, contact) => {
    if (!contact) return list || [];
    const prev = Array.isArray(list) ? list : [];
    if (contact._id && prev.some(c => String(c._id) === String(contact._id))) return prev;
    return [...prev, contact];
  };

  const removeContact = (list, contact) => {
    if (!contact) return list || [];
    const prev = Array.isArray(list) ? list : [];
    if (!contact._id) return prev.filter(c => c !== contact);
    return prev.filter(c => String(c._id) !== String(contact._id));
  };

  const replaceContact = (list, oldContact, newContact) => {
    if (!oldContact || !newContact) return list || [];
    return (list || []).map(c => {
      if (oldContact._id && c._id && String(c._id) === String(oldContact._id)) return newContact;
      if (!oldContact._id && c === oldContact) return newContact;
      return c;
    });
  };

  const linkUnlinkContact = (contact, unlink = false) => {
    const next = unlink
      ? removeContact(managementCompanyContacts, contact)
      : mergeContact(managementCompanyContacts, contact);
    setManagementCompanyContacts(next);
    setHoa(prev => ({ ...prev, managementCompanyContacts: next }));
  };

  const updateContact = (oldContact, newContact) => {
    const next = replaceContact(managementCompanyContacts, oldContact, newContact);
    setManagementCompanyContacts(next);
    setHoa(prev => ({ ...prev, managementCompanyContacts: next }));
  };

  const validate = () => {
    const newErrors = {};
    const companyError =
      getDirectoryNameError(hoa?.managementCompany, 'Management company') ||
      (!hoa?.managementCompany?.trim() ? 'Management company name is required' : null);
    if (companyError) newErrors.name = true;

    const emailError = getEmailValidationError(hoa?.companyEmail);
    if (emailError) newErrors.companyEmail = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hoaForSave = () => ({
    ...hoa,
    managementCompanyContacts,
    _pendingManagementFiles: pendingManagementFiles,
    _removedManagementCompanyFileIds: removedManagementFileIds,
  });

  const saveCreate = async () => {
    const payload = prepareManagementCompanyPayload(hoaForSave());
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.MANAGEMENT_COMPANIES,
      method: 'POST',
      requestBody: payload,
    });
    if (error) throw new Error(error);

    if (pendingManagementFiles.length) {
      await uploadInstructionFiles(
        ENDPOINTS.MANAGEMENT_COMPANY_INSTRUCTION_FILES(response._id),
        pendingManagementFiles
      );
    }

    const { response: fresh, error: fetchError } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.MANAGEMENT_COMPANY_WITH_ID(response._id),
      method: 'GET',
    });
    if (fetchError) throw new Error(fetchError);
    return fresh || response;
  };

  const saveEdit = async () => {
    const id = resolveEntityId(managementCompanyId);
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.MANAGEMENT_COMPANY_WITH_ID(id),
      method: 'PUT',
      requestBody: prepareManagementCompanyPayload(hoaForSave()),
    });
    if (error) throw new Error(error);

    const linkedAssociationId = resolveEntityId(associationId);
    if (linkedAssociationId) {
      const { error: associationError } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(linkedAssociationId),
        method: 'PUT',
        requestBody: { orderingPlatform: hoa.orderingPlatform || '' },
      });
      if (associationError) throw new Error(associationError);
    }

    if (pendingManagementFiles.length) {
      await uploadInstructionFiles(
        ENDPOINTS.MANAGEMENT_COMPANY_INSTRUCTION_FILES(response._id),
        pendingManagementFiles
      );
    }

    const removedIds = removedManagementFileIds || [];
    let saved = response;
    if (pendingManagementFiles.length || removedIds.length) {
      const { response: fresh, error: fetchError } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.MANAGEMENT_COMPANY_WITH_ID(response._id),
        method: 'GET',
      });
      if (!fetchError && fresh) {
        saved = fresh;
      }
    }

    return saved;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    try {
      const saved = isEdit ? await saveEdit() : await saveCreate();
      toast.success(
        isEdit ? 'Management company updated successfully' : 'Management company created successfully'
      );
      onHide();
      onSaved?.(saved, { orderingPlatform: hoa.orderingPlatform || '' });
    } catch (err) {
      toast.error(err.message || `Failed to ${isEdit ? 'update' : 'create'} management company`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formReady = !isEdit || (hoa && !loading);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="add-directory-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Management Company' : 'Add Management Company'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {!formReady ? (
          <HoaBlockSkeleton />
        ) : (
          <ManagementCompanyFormFields
            index={0}
            hoa={hoa}
            onChange={updateHoa}
            fieldInvalid={{
              company: !!errors.name,
              companyEmail: !!errors.companyEmail,
            }}
            managementCompanyRequired
            managementCompanyCreateMode={useNameInput}
            directoryCreateMode={!isEdit}
            directoryEditMode={isEdit}
            managementCompanyContacts={managementCompanyContacts}
            managementCompanyFiles={managementCompanyFiles}
            pendingManagementFiles={pendingManagementFiles}
            removedManagementFileIds={removedManagementFileIds}
            onSelectCompany={() => {}}
            onCreateCompany={() => {}}
            onLinkContact={contact => linkUnlinkContact(contact)}
            onUnlinkContact={contact => linkUnlinkContact(contact, true)}
            onUpdateContact={updateContact}
            onPendingFilesChange={setPendingManagementFiles}
            onRemovedFileIdsChange={setRemovedManagementFileIds}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !formReady}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Saving...
            </>
          ) : isEdit ? (
            'Save Changes'
          ) : (
            'Create Company'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DirectoryManagementCompanyModal;
